import React, { useEffect, useState } from "react";
import TitleAndCloseComponent from "../../Components/TitleAndCloseComponent";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import MuiTypographyMandatory from "../../Components/Custom/MuiTypographyMandatory";
import StyledInputField from "../../Components/Custom/StyledInputField";
import { VM_Colors } from "../../Styles/VMStyles";
import { API_ADD_ROLES, API_GET_ALL_PERMISSIONS, API_UPDATES_ROLE } from "../../Util/URLConstants";
import axios from "axios";
import { toast } from "react-toastify";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_ID_KEY } from "../../Util/VMConstants";

function AddRolePermissions({ onClose,onReload,  editData = null }) {
  // ---------------- STATE ----------------
  const [permissionsList, setPermissionsList] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [roleType, setRoleType] = useState("");
  const [customRoleName, setCustomRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(false);



    const sessionFactory = new StorageFactory("session");
    const user_id = sessionFactory.get(SESSION_USER_ID_KEY);
  console.log("editData:",editData);

  const editId = editData?.id || null;
  const role_name = editData?.role_name || null;

  // ---------------- API CALL ----------------
  const fetchPermissions = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_GET_ALL_PERMISSIONS);
      setPermissionsList(res.data.permissions || []);
    } catch (err) {
      console.error("Error fetching permissions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();

    if (editData) {
      // Prefill data when editing
      setRoleType(
        ["Manager", "Executive", "Receptionist", "Security Guard"].includes( role_name )
          ? role_name
          : "Others"
      );
      setCustomRoleName(
        ["Manager", "Executive", "Receptionist", "Security Guard"].includes( role_name )
          ? ""
          : role_name
      );
      setRoleDescription(editData.description || "");
      setSelectedPermissions(editData.permissions?.map((p) => p.id) || []);
    } else {
      // Reset form
      setRoleType("");
      setCustomRoleName("");
      setRoleDescription("");
      setSelectedPermissions([]);
    }
  }, [editData, role_name]);

  // ---------------- HANDLERS ----------------
  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const handleSelectAllPermissions = (checked) => {
    setSelectAll(checked);
    setSelectedPermissions(
      checked ? permissionsList.map((perm) => perm.id) : []
    );
  };

  
  const handleSaveRole = async () => {
    const newErrors = {};
    if (!roleType) {
      newErrors.roleType = "Please select a role";
      toast.error("Please select a role");
    }
    if (roleType === "Others" && !customRoleName.trim()) {
      newErrors.customRoleName = "Enter custom role name";
      toast.error("Custom role name is required");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const finalRoleName = roleType === "Others" ? customRoleName.trim() : roleType;

    const permissions = permissionsList
      .filter((perm) => selectedPermissions.includes(perm.id))
      .map((perm) => ({
        id: perm.id,
        permission_id: perm.permission_id,
        name: perm.name,
      }));

    const body = {
      user_id,
      name: finalRoleName,
      description: roleDescription,
      permissions,
    };

    try {
      setLoading(true);
      const response = editId
        ? await axios.put(`${API_UPDATES_ROLE}/${editId}`, body)
        : await axios.post(API_ADD_ROLES, body);

      const respData = response.data;
      if (respData.status_code === 200) {
        onClose();
        onReload();
      } else {
        toast.error(respData?.message);
      }
    } catch (err) {
      console.error("Failed to save role:", err);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box
      sx={{ backgroundColor: VM_Colors.white, pl: 2, pr: 2, position: "relative" }}
    >
      <TitleAndCloseComponent
        title={editId ? "Update Role" : "Add New Role"}
        onClick={onClose}
        bottomBorderLine={false}
      />
      <Box ml={2} mr={3}>
        <Stack spacing={3}>
          {/* Role Type Dropdown */}
          <FormControl fullWidth>
            <MuiTypographyMandatory label="Role Type" required />
            <Select
              value={roleType}
              onChange={(e) => {
                setRoleType(e.target.value);
                setErrors((prev) => ({ ...prev, roleType: undefined }));
                if (e.target.value !== "Others") setCustomRoleName("");
              }}
              // disabled={!!editId}
              displayEmpty
              error={!!errors.roleType}
            >
              <MenuItem value="" disabled>  -- Select --   </MenuItem>
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="Executive">Executive</MenuItem>
              <MenuItem value="Receptionist">Receptionist</MenuItem>
              <MenuItem value="Security Guard">Security Guard</MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </Select>
            {errors.roleType && (
              <Typography variant="caption" color="error">
                {errors.roleType}
              </Typography>
            )}
          </FormControl>

          {/* Custom Role Name if Others */}
          {roleType === "Others" && (
            <StyledInputField
              heading="Custom Role Name"
              required
              fullWidth
              // disabled={!!editId}
              value={customRoleName}
              onChange={(e) => {
                setCustomRoleName(e.target.value);
                setErrors((prev) => ({ ...prev, customRoleName: undefined }));
              }}
              error={!!errors.customRoleName}
              helperText={errors.customRoleName}
            />
          )}

          <StyledInputField
            heading="Description"
            fullWidth
            multiline
            rows={3}
            value={roleDescription}
            onChange={(e) => {
              setRoleDescription(e.target.value);
              setErrors((prev) => ({ ...prev, roleDescription: undefined }));
            }}
            error={!!errors.roleDescription}
            helperText={errors.roleDescription}
          />
        </Stack>

        <MuiTypographyMandatory
          label={"Permissions"}
          required={true}
          variant="subtitle2"
          fontWeight={700}
          mt={2}
        />

        {!editId && (
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAll}
                onChange={(e) => handleSelectAllPermissions(e.target.checked)}
              />
            }
            label="Select All Permissions"
          />
        )}

        {Object.entries(
          permissionsList.reduce((acc, perm) => {
            const category = perm.category || "Uncategorized";
            if (!acc[category]) acc[category] = [];
            acc[category].push(perm);
            return acc;
          }, {})
        ).map(([category, perms]) => (
          <Box key={category} sx={{ mb: 3 }}>
            <Typography variant="h5" mb={1}>
              {category}
            </Typography>
            <Grid container spacing={2}>
              {perms.map((perm) => (
                <Grid item xs={12} sm={6} key={perm.id}>
                  <Card
                    variant="outlined"
                    sx={{ borderRadius: 2, p: 1, minWidth: 230 }}
                  >
                    <CardContent sx={{ p: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedPermissions.includes(perm.id)}
                            onChange={() => handlePermissionToggle(perm.id)}
                          />
                        }
                        label={
                          <Box>
                            <Typography fontSize={14} fontWeight={600}>
                              {perm.name}
                            </Typography>
                            {perm.description && (
                              <Typography fontSize={12} color="text.secondary">
                                {perm.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {/* Footer Actions */}
        <Box
          sx={{ display: "flex", justifyContent: "space-between", mt: 4, gap: 2 }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              flex: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              color: VM_Colors.primary,
              borderColor: VM_Colors.primary,
              px: 4,
              py: 1.5,
              "&:hover": {
                borderColor: VM_Colors.primary,
                backgroundColor: `${VM_Colors.primary}14`,
              },
            }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveRole}
            sx={{
              flex: 1,
              borderRadius: 2,
              px: 4,
              py: 1.5,
            }}
          >
            {editId ? "Update Role" : "Save Role"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default AddRolePermissions;
